/**
 * game-kingyo.js — one poi, one tank, and whatever you can lift out of it.
 *
 * THE POI IS YOUR FINGER, and the whole game is one gesture:
 *
 *   press    the poi goes into the water where you touched
 *   glide    it follows your hand — slowly, or the paper goes
 *   let go   it comes up, and whatever was travelling with it comes too
 *
 * There is no second gesture and no button to press while playing. A mouse
 * does exactly the same thing with the button held down, which is why there is
 * one set of pointer handlers here and not two.
 *
 * EVERYTHING HAPPENS ON POINTERDOWN AND POINTERUP, never on click: a click
 * only fires when the press and the release land on the same element, and this
 * stage is redrawn every frame — so a real hand's click arrives at the page
 * root, where nobody is listening. Mochi Pound shipped that bug once.
 */
(function () {
  'use strict';

  var Art = window.KingyoArt;
  var root = document.querySelector('[data-kingyo-game]');
  if (!root) return;

  var TANK = Art && Art.TANK;

  /* WATER IS THICK. The poi chases the hand rather than teleporting to it, and
     it cannot be dragged through the tank faster than this however hard the
     hand goes — which is what being under water feels like, and what stops a
     thrown finger from carrying the hoop across the tank in one frame without
     touching anything on the way.

     IT IS ALSO THE WORST CASE THE PAPER CAN BE MADE TO PAY. Wear climbs with
     the square of speed, so the cap is what bounds it: at 470 a single jumpy
     frame cost 2.7 of the paper a second and a stutter could take a quarter of
     the round's whole budget with it. At 300 the same accident costs a fifth
     of that, and the poi still crosses the tank in a second. */
  var POI_MAX = 300;          // tank units a second

  /* HOW LONG THE POI REMEMBERS ITS OWN SPEED, in seconds.
     A hand's velocity has to be SMOOTHED, not sampled. Pointer events do not
     arrive every frame — a coalesced move, a slow frame, a phone deciding to
     do something else — so the instantaneous speed of a hand that is tracking
     a fish perfectly reads as ZERO on maybe half of all frames. And the catch
     is tested at one instant, the moment of the lift: unsmoothed, a lift that
     landed on one of those frames failed for a player who had done everything
     right, and there was no way to tell that from the game being hard.
     A tenth of a second of memory is shorter than any deliberate change of
     direction and longer than any gap between events. */
  var VEL_MEMORY = 0.09;

  /* HOW FAR ABOVE A THUMB THE SCOOP SITS, in tank units.
     A finger on a phone covers about forty-five pixels of screen in every
     direction, and the thing this game asks you to look at is the fish UNDER
     the hoop. Put the hoop under the thumb and the player is being asked to
     aim at something her own hand is covering — which is what shipped, and it
     is not a difficulty, it is a blindfold.
     So on a touchscreen the hoop rides above the finger, with a line drawn
     between them so the arrangement explains itself in one press. A mouse
     needs none of this: a cursor does not cover anything. */
  var THUMB_LIFT = 62;

  /* THE HUB'S DAILY CARD LINKS HERE WITH `?daily=1`, and this page ignored it.
     `GAMES.kingyo.daily` has been `true` since the day it shipped, and until
     Starlight Steps went live it was the ONLY live game with that flag — so
     `dailyChallenge()` handed out Kingyo-sukui every single day, and
     `startRun` only marks a run as the daily when the page asks for one. No
     `daily_plays` row has ever been written: the challenge card's fifty points
     have never been payable and the `daily-10` badge has been unearnable. The
     streak survived it, because that was changed to count any finished run.
     Which is the same failure the comment above `dailyChallenge()` in
     worker.js describes, arrived at from the other end — that one was a live
     game with no page, this one is a page that never asks. */
  var WANTS_DAILY = /(?:\?|&)daily=1(?:&|$)/.test(window.location.search);

  /** Today, as everybody's clock agrees on it. */
  function dayKey() {
    return new Date().toISOString().slice(0, 10);
  }

  var S = {
    view: 'ready',            // ready | playing | over
    round: null,
    hand: { x: 0, y: 0, down: false, v: 0, vx: 0, vy: 0 },
    want: { x: 0, y: 0 },     // where the poi is being asked to go
    grip: { x: 0, y: 0 },     // ...and where the hand actually is
    thumb: false,             // a finger, rather than a mouse
    run: null,                // the ledger's handle, when there is one
    runP: null,               // and the promise of it, which may still be in the air
    daily: false,             // is this round today's challenge?
    dailyDone: false,         // and has today's go been used up?
    result: null,
    say: null,
    caught: [],               // what is in the bowl, newest last
    flash: null,              // a fish just landed: { key, at }
  };

  /* ---- the clock ------------------------------------------------------ */

  var raf = null, last = 0;

  function tick(now) {
    raf = null;
    if (S.view !== 'playing' || !S.round) return;

    /* A TAB THAT WAS IN THE BACKGROUND comes back with a gap of minutes in it.
       Handing that to the model as one step would tear the paper instantly and
       the player would never know why. */
    var dt = Math.min(0.05, (now - last) / 1000 || 0);
    last = now;

    /* The poi swims toward the hand. What the model is told about speed is
       what the poi ACTUALLY did, not what the finger did — otherwise a flick
       costs paper the hoop never spent. */
    var dx = S.want.x - S.hand.x, dy = S.want.y - S.hand.y;
    var d = Math.hypot(dx, dy);
    var step = Math.min(d, POI_MAX * dt);
    var ivx = 0, ivy = 0;
    if (d > 0.0001 && step > 0) {
      S.hand.x += (dx / d) * step;
      S.hand.y += (dy / d) * step;
      ivx = (dx / d) * (step / dt);
      ivy = (dy / d) * (step / dt);
    }
    /* TWO SMOOTHINGS, BECAUSE THEY ANSWER DIFFERENT QUESTIONS.
       The direction the poi is going is a VECTOR average — that is what a fish
       has to be travelling with, and it is what a missing pointer event must
       not be allowed to read as a stop.
       How hard it is being dragged is a SCALAR average of the speed. Averaging
       the vector for that was wrong and the drive caught it: a poi shaken from
       one corner to the other cancels to nearly nothing as a vector, so the
       most violent thing a hand can do to wet paper was costing less than a
       gentle glide. A magnitude cannot cancel. */
    var keep = Math.exp(-dt / VEL_MEMORY);
    S.hand.vx = S.hand.vx * keep + ivx * (1 - keep);
    S.hand.vy = S.hand.vy * keep + ivy * (1 - keep);
    S.hand.v = S.hand.v * keep + Math.hypot(ivx, ivy) * (1 - keep);

    Art.step(S.round, dt, S.hand);
    if (S.round.torn) return done('torn');

    paintTank();
    paintPaper();
    raf = window.requestAnimationFrame(tick);
  }

  function run() {
    if (raf) return;
    last = window.performance.now();
    raf = window.requestAnimationFrame(tick);
  }
  function halt() {
    if (raf) window.cancelAnimationFrame(raf);
    raf = null;
  }

  /* ---- the gesture ---------------------------------------------------- */

  /**
   * Where the SCOOP goes for a hand at this point.
   *
   * Above a finger, and exactly at a mouse. Clamped to the tank, so pressing
   * near the top does not send the hoop through the ceiling — the line drawn
   * between hand and hoop keeps saying which is which even when the offset has
   * been squeezed to nothing.
   */
  function holdPoint(p) {
    if (!S.thumb) return { x: p.x, y: p.y };
    var r = Art.POND.poi.r;
    return {
      x: Math.min(Math.max(p.x, r), TANK.w - r),
      y: Math.min(Math.max(p.y - THUMB_LIFT, r), TANK.h - r),
    };
  }

  /** Where the pointer is, in tank units. */
  function pointIn(ev, svg) {
    var box = svg.getBoundingClientRect();
    return {
      x: (ev.clientX - box.left) * (TANK.w / box.width),
      y: (ev.clientY - box.top) * (TANK.h / box.height),
    };
  }

  var held = null;

  function wireStage() {
    var stage = root.querySelector('[data-tank]');
    if (!stage || stage.dataset.wired) return;
    stage.dataset.wired = '1';

    stage.addEventListener('pointerdown', function (ev) {
      if (ev.button && ev.button !== 0) return;          // a right-click is not a hand
      if (S.view !== 'playing' || !S.round) return;
      var svg = stage.querySelector('svg');
      if (!svg) return;
      ev.preventDefault();
      var p = pointIn(ev, svg);
      var wasThumb = S.thumb;
      S.thumb = ev.pointerType !== 'mouse';
      if (wasThumb !== S.thumb) paintHint();
      S.grip = p;
      p = holdPoint(p);
      /* IT GOES IN WHERE SHE PUT IT. No easing on the way down: a poi that
         slides across the tank to meet her finger is a poi that has already
         frightened everything between here and there. */
      S.want = p;
      S.hand.x = p.x; S.hand.y = p.y;
      S.hand.vx = 0; S.hand.vy = 0; S.hand.v = 0;
      S.hand.down = true;
      held = ev.pointerId;
      /* Both averages start from a standstill, so the poi never arrives in the
         water carrying the speed of the hand that was waving about above it. */
      try { stage.setPointerCapture(ev.pointerId); } catch (e) { /* fine */ }
      paintTank();
    });

    stage.addEventListener('pointermove', function (ev) {
      if (S.view !== 'playing' || !S.round) return;
      var svg = stage.querySelector('svg');
      if (!svg) return;
      var p = pointIn(ev, svg);
      if (held !== null && ev.pointerId === held) {
        ev.preventDefault();
        S.grip = p;
        S.want = holdPoint(p);
        return;
      }
      /* A MOUSE IS HOVERING ALL THE TIME and can be shown where the poi would
         go; a finger only ever arrives. So the hoop follows an un-pressed
         mouse around above the water, which is also how she can see how big it
         is before she commits to anything. */
      if (ev.pointerType === 'mouse' && !S.hand.down) {
        S.thumb = false;
        S.grip = p;
        S.want = p; S.hand.x = p.x; S.hand.y = p.y;
        paintTank();
      }
    });

    /* ONE WAY OUT, AND EVERY DOOR LEADS TO IT.
       A press that never ends is a game that never gives you your fish, and
       that is what a phone reported: the fish would settle, and letting go did
       nothing at all. A pointerup is only one of the ways a browser can tell
       you a finger has gone — it can cancel the pointer instead, it can hand
       the up to a different target, it can give the same finger a new id — so
       the lift listens for all of them, and for a plain touchend underneath as
       the one that cannot be argued with. Ending twice is impossible: `held`
       is cleared first and every door checks it. */
    function letGo(ev) {
      if (held === null) return;
      /* An up for some OTHER finger is not this press ending. One with no id
         at all is the touch safety net, and it is always this press. */
      if (ev && ev.pointerId != null && ev.pointerId !== held && ev.isPrimary === false) return;
      var id = held;
      held = null;
      try { stage.releasePointerCapture(id); } catch (e) { /* fine */ }
      if (S.view !== 'playing' || !S.round) return;

      var got = Art.lift(S.round, S.hand);
      S.hand.down = false;
      S.hand.vx = 0; S.hand.vy = 0; S.hand.v = 0;

      if (got.kept.length) {
        S.caught = S.caught.concat(got.kept);
        S.flash = { key: got.kept[got.kept.length - 1], at: Date.now() };
      }
      paintTank();
      paintPaper();
      paintBowl();
      if (S.round.torn) return done('torn');
      if (!S.round.fish.some(function (f) { return !f.gone; })) return done('empty');
    }
    stage.addEventListener('pointerup', letGo);
    /* ON THE WINDOW TOO, because an up delivered to something other than the
       tank is still her finger leaving the glass. Capture phase, so it is seen
       whatever else the page does with it. */
    window.addEventListener('pointerup', letGo, true);
    /* A CANCELLED POINTER IS A LIFT NOW, and that is a reversal worth the
       words. It used to put the fish quietly back, on the reasoning that the
       browser taking the pointer away is not a decision she made. True — but a
       phone that cancels instead of ending the pointer turns that reasoning
       into a game where nothing is ever caught, which is exactly the report
       that came back: the fish settle and letting go does nothing. Between
       handing her a fish she was already holding and never handing her one at
       all, the first is the honest reading of what her hand did. A lift with
       nothing aboard costs nothing, so the worst case is a poi that comes out
       of the water empty. */
    window.addEventListener('pointercancel', letGo, true);
    /* AND THE ONE THAT CANNOT BE ARGUED WITH. If the pointer layer loses the
       press entirely, the touch layer underneath it still says the finger has
       gone. Nothing else can end a press that the browser has stopped talking
       about. */
    stage.addEventListener('touchend', letGo);
    stage.addEventListener('touchcancel', letGo);
    /* AND THE GESTURE STAYS HERE. `touch-action: none` is meant to be enough
       and mostly is; preventing the touch itself is what stops a browser that
       has decided the drag was a scroll from cancelling the pointer halfway
       through a fish. Not passive, or preventDefault does nothing. */
    stage.addEventListener('touchstart', function (ev) { ev.preventDefault(); }, { passive: false });
    stage.addEventListener('touchmove', function (ev) { ev.preventDefault(); }, { passive: false });
  }

  /* ---- starting and stopping ------------------------------------------ */

  /**
   * A round.
   *
   * `opts` IS HOW THE DAILY GETS IN. A normal go opens its own run and makes
   * its own tank; today's challenge has to open the run FIRST, because the
   * tank comes out of the run's seed and that is the only way everybody scoops
   * the same one. Called with nothing, this is exactly what it always was.
   */
  async function begin(opts) {
    opts = opts || {};
    S.daily = !!opts.daily;
    S.round = Art.roundFor(opts.seed || seed());
    S.caught = [];
    S.result = null;
    S.say = null;
    S.flash = null;
    S.hand = { x: TANK.w / 2, y: TANK.h * 0.62, down: false, v: 0, vx: 0, vy: 0 };
    S.want = { x: S.hand.x, y: S.hand.y };
    S.view = 'playing';
    paintShell();

    /* THE LEDGER IS OPTIONAL AND THE GAME IS NOT. A guest, an undeployed
       route or a dropped connection must not stand between a child and a tank
       of goldfish — she plays, and what is not banked is simply not banked. */
    S.run = null;
    S.runP = opts.handle ? Promise.resolve(opts.handle)
      : (window.Arcade ? window.Arcade.start('kingyo') : Promise.resolve(null));
    S.runP.then(function (h) { S.run = h; }, function () { S.run = null; });
    run();
  }

  /** A tank of her own — which is every round except today's challenge. */
  function seed() {
    return 'kingyo-' + Date.now() + '-' + Math.floor(Math.random() * 1e6);
  }

  /**
   * Today's tank, once.
   *
   * THE RUN IS OPENED FIRST AND THE TANK COMES OUT OF IT. `roundFor` is
   * already a function of a seed — that is how the harness replays a round —
   * so the only thing standing between this game and a real daily was asking
   * the arcade for one and using the seed it answers with.
   */
  function beginDaily() {
    S.say = null;
    var day = dayKey();
    if (!window.Arcade) return begin({ seed: day, daily: false });
    window.Arcade.start('kingyo', { daily: true, seed: day }).then(function (h) {
      /* THE ARCADE'S OWN SEED WHEN IT IS REALLY TODAY'S CHALLENGE, AND THE DATE
         OTHERWISE. `/run/start` only counts a run as the daily when this game
         is the one the arcade picked for today, and hands back a random seed
         when it is not — so on any other day that seed would make "the same
         tank for everybody" a different tank for every visitor. The date is
         the same string on every phone. */
      begin({ seed: h.daily ? h.seed : day, handle: h, daily: !!h.daily });
    }, function (err) {
      /* ALREADY SCOOPED IT TODAY — or logged out, or the arcade is down. This
         is the branch that made the one-line version of this fix a bad idea:
         claiming the daily means a second go gets `daily_done` back, and
         before this there was nowhere for that to be said, so it would have
         become a silently unbanked round. */
      S.say = (err && err.message) || 'Today\u2019s tank is not available right now.';
      S.dailyDone = true;
      S.view = 'ready';
      paintShell();
    });
  }

  function done(how) {
    halt();
    S.view = 'over';
    S.hand.down = false;
    /* A FISH STILL IN THE PAPER WHEN THE ROUND ENDS IS NOT A FISH SHE LANDED.
       Stopping while ahead is a decision about the bowl, not about the water,
       so whatever had settled goes back to swimming rather than counting. */
    if (S.round) Art.release(S.round);
    S.result = {
      how: how,                       // torn | stopped | empty
      fish: S.caught.slice(),
      score: Art.scoreOf(S.caught),
    };
    paintShell();

    /* THE RUN MAY STILL BE IN THE AIR. It is opened when the round starts and
       read here, at the end — and on poor mobile data `/run/start` can take
       longer than a torn poi does. Read once and found null, the whole round
       was thrown away without a word, which is the one failure a scored game
       must not have. */
    var pending = S.runP;
    Promise.resolve(pending).then(function (handle) {
      if (pending === S.runP) bank(handle);
    }, function () {
      if (pending === S.runP) bank(null);
    });
  }

  /** Writing a finished round down, once there is a run to write it against. */
  function bank(handle) {
    if (!handle) {
      S.say = 'That did not save, but the fish are yours.';
      return paintOver();
    }
    /* The wrapper already toasts a badge, a drop and a familiar — doing it
       here as well is the same news twice. */
    handle.finish(S.result.score, { fish: S.caught.slice() }).then(function (res) {
      /* A FAILURE IS A FIELD ON THE ANSWER, NOT A REJECTION. arcade-core.js
         hands back a guest handle when nobody is logged in and swallows a
         failed `/run/finish` into `banked: false` rather than throwing — so
         the message below used to be unreachable code, and a round that did
         not save looked exactly like one that did. */
      if (res && res.banked === false) {
        S.say = res.guest ? 'Log in to keep a score.'
          : 'That did not save, but the fish are yours.';
        return paintOver();
      }
      if (S.daily) S.dailyDone = true;
      S.result.banked = res;
      paintOver();
    }, function () {
      /* She still caught them. What she has not got is them written down. */
      S.say = 'That did not save, but the fish are yours.';
      paintOver();
    });
  }

  /* ---- drawing -------------------------------------------------------- */

  function paintTank() {
    var stage = root.querySelector('[data-tank]');
    if (!stage || !S.round) return;
    /* WHERE THE HAND IS, so the art can draw the stick to it. On a
       touchscreen the hoop rides above the finger, and the thing that explains
       that is the handle: hold this end, the hoop is on the other. It used to
       be a dashed line drawn on afterwards, going from the hoop to the finger
       — a second mark for a relationship the stick already describes, and the
       player read it the only way it could be read, as her finger being on the
       net. A mouse gets no grip and keeps the angled handle: a cursor is not
       holding anything. */
    S.hand.grip = (S.thumb && S.hand.down) ? { x: S.grip.x, y: S.grip.y } : null;
    stage.innerHTML = Art.scene(S.round, S.hand);
  }

  /** The paper, said in words as well as drawn — some players never look. */
  function paintPaper() {
    var el = root.querySelector('[data-paper]');
    if (!el || !S.round) return;
    var k = S.round.paper;
    var word = k > 0.66 ? 'The paper is holding' : k > 0.33 ? 'The paper is going soft' : 'The paper is nearly gone';
    el.textContent = word;
    el.className = 'kg-paper' + (k > 0.66 ? '' : k > 0.33 ? ' is-soft' : ' is-thin');
  }

  function paintBowl() {
    var el = root.querySelector('[data-bowl]');
    if (!el) return;
    if (!S.caught.length) {
      el.innerHTML = '<p class="kg-bowl__empty">Nothing in the bowl yet.</p>';
      return;
    }
    el.innerHTML = S.caught.map(function (k) {
      return '<span class="kg-bowl__one' + (S.flash && S.flash.key === k ? ' is-new' : '') + '">'
        + Art.portrait(k, 42) + '</span>';
    }).join('');
  }

  /** The varieties this round added to her collection, as the worker saw it. */
  function fresh() {
    var banked = S.result && S.result.banked;
    var caught = (banked && banked.caught) || [];
    return caught.filter(function (c) { return c && c.first; }).map(function (c) { return c.fish; });
  }

  function paintOver() {
    var el = root.querySelector('[data-over]');
    if (!el || !S.result) return;
    var r = S.result;
    var kinds = {};
    r.fish.forEach(function (k) { kinds[k] = (kinds[k] || 0) + 1; });
    var names = Object.keys(kinds).map(function (k) {
      return Art.fishOf(k).name + (kinds[k] > 1 ? ' ×' + kinds[k] : '');
    });

    el.innerHTML =
      '<h2 class="kg-over__what">' + (r.how === 'torn'
        ? 'The paper went.'
        : r.how === 'empty' ? 'You cleared the tank.' : 'You stopped while you were ahead.') + '</h2>'
      /* WHICH ONES SHE HAD NEVER LANDED BEFORE. The worker is the only thing
         that knows — a collection is only a collection because some of it is
         missing — so this line appears a moment after the rest, when the round
         comes back from the ledger. */
      + (r.fish.length
        ? '<div class="kg-over__fish">' + r.fish.map(function (k) { return Art.portrait(k, 56); }).join('') + '</div>'
          + '<p class="kg-over__names">' + esc(names.join(' · ')) + '</p>'
          + (fresh().length
            ? '<p class="kg-new">' + (fresh().length === 1 ? 'New for the collection: ' : 'New for the collection: ')
              + esc(fresh().map(function (k) { return Art.fishOf(k).name; }).join(', ')) + '</p>'
            : '')
          + '<p class="kg-fine"><b>' + r.score + '</b> points'
            + (r.banked && r.banked.points_earned ? ' — ' + r.banked.points_earned + ' into the ledger' : '') + '</p>'
        : '<p class="kg-over__names">Not one. The paper does not forgive hurrying.</p>')
      + (S.say ? '<p class="kg-fine">' + esc(S.say) + '</p>' : '')
      + (S.daily
          ? '<p class="kg-fine">That was today\u2019s tank. A new one tomorrow.</p>'
            + '<button type="button" class="kg-btn" data-again>Have a practice go</button>'
          : '<button type="button" class="kg-btn" data-again>Play again</button>')
      + '<a class="kg-btn kg-btn--link" href="/pages/arcade">Back to the arcade</a>';
  }

  /* NO WAY OUT DRAWN IN HERE. Every arcade section renders the shared link
     above the game — snippets/arcade-back.liquid — so a second one in this
     game's own bar is two buttons a centimetre apart doing one job.
     Barb, twice: "Remove the second back button that says Arcade." */

  function paintShell() {
    if (S.view === 'ready') {
      root.innerHTML = '<div class="kg">'
        + '<div class="kg-card kg-card--intro">'
        + '<h2>One paper scoop.</h2>'
        + '<p>Put it in the water and <b>go with a fish</b> — not at it. Match how '
        + 'it swims, then let go, and it comes up with you.</p>'
        + '<p class="kg-fine">Hurry and the paper tears. It only tears once.</p>'
        + (S.say ? '<p class="kg-fine">' + esc(S.say) + '</p>' : '')
        /* TODAY'S TANK ABOVE THE ORDINARY GO, and only when the hub asked for
           it. A daily offered to somebody who came here off the tile would be
           a promise the arcade has not made: `/run/start` only counts it when
           this game is the one it picked for today. */
        + (WANTS_DAILY && !S.dailyDone
            ? '<div class="kg-daily"><em class="kg-dailytag">Today\u2019s tank</em>'
              + '<p>Everybody scoops the same one today — the same fish in the '
              + 'same water. One go.</p>'
              + '<button type="button" class="kg-btn" data-do="daily">'
              + 'Scoop today\u2019s tank</button></div>'
            : '')
        + '<button type="button" class="kg-btn' + (WANTS_DAILY && !S.dailyDone
            ? ' kg-btn--quiet' : '') + '" data-begin>'
        + (WANTS_DAILY && !S.dailyDone ? 'Just a practice go' : 'Start') + '</button>'
        + '</div></div>';
      return;
    }

    if (S.view === 'over') {
      root.innerHTML = '<div class="kg">'
        + '<div class="kg-card" data-over></div>'
        + '</div>';
      paintOver();
      return;
    }

    root.innerHTML = '<div class="kg">'
      + '<div class="kg-bar">'
        + '<span class="kg-paper" data-paper>The paper is holding</span>'
        + '<button type="button" class="kg-stop" data-stop>That’ll do</button>'
      + '</div>'
      + '<div class="kg-stage" data-tank></div>'
      + '<div class="kg-bowl" data-bowl></div>'
      + '<p class="kg-hint" data-hint></p>'
      + '</div>';
    paintTank();
    paintPaper();
    paintBowl();
    paintHint();
    wireStage();
  }

  /* THE HINT KNOWS WHICH HAND IT IS TALKING TO. 'The scoop sits above your
     finger' is essential on a phone and nonsense on a laptop, and a game that
     says both says neither. */
  function paintHint() {
    var el = root.querySelector('[data-hint]');
    if (!el) return;
    el.textContent = S.thumb
      ? 'Your finger is the end of the stick — hold the scoop under a fish and go along with it until the ring closes, then let go.'
      : 'Hold on a fish and go along with it until the ring closes, then let go.';
  }

  function esc(t) {
    return String(t == null ? '' : t).replace(/[&<>]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c];
    });
  }

  /* DELEGATED ON THE ROOT, which is the one element paintShell never replaces.
     A handler on a button inside the shell is thrown away the first time the
     shell is redrawn, and the button stops working with nothing in the console
     to say why. */
  root.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-do="daily"]')) { ev.preventDefault(); beginDaily(); return; }
    /* AND `begin()` WITH NOTHING IS A PRACTICE GO. Which is what it always
       was, and what "Play again" after the daily has to be: today's challenge
       is once a day, and the game is not. */
    if (t.closest('[data-begin]') || t.closest('[data-again]')) {
      ev.preventDefault(); S.say = null; begin(); return;
    }
    if (t.closest('[data-stop]')) { ev.preventDefault(); if (S.view === 'playing') done('stopped'); }
  });

  /* A tab that goes away mid-round should not come back to a torn poi. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) halt();
    else if (S.view === 'playing') run();
  });

  if (!Art) {
    root.innerHTML = '<div class="kg"><p class="kg-quiet">The tank could not load.</p></div>';
  } else {
    paintShell();
  }

  /* The test seam. `hold` is what a hand does, without a hand — so a driver
     can prove the RULES separately from the gesture, which is proved with a
     real mouse in art/kingyo-harness/drive-kingyo.mjs. */
  window.Kingyo = {
    state: function () { return S; },
    begin: begin,
    beginDaily: beginDaily,
    wantsDaily: function () { return WANTS_DAILY; },
    day: dayKey,
    stop: function () { if (S.view === 'playing') done('stopped'); },
    /** Put the poi at a point, travelling at a velocity, for one tick. */
    hold: function (x, y, vx, vy, dt) {
      S.hand.x = x; S.hand.y = y; S.hand.vx = vx || 0; S.hand.vy = vy || 0;
      S.hand.v = Math.hypot(S.hand.vx, S.hand.vy);
      S.hand.down = true;
      S.want = { x: x, y: y };
      Art.step(S.round, dt || 1 / 60, S.hand);
    },
    lift: function () {
      var got = Art.lift(S.round, S.hand);
      S.hand.down = false;
      if (got.kept.length) S.caught = S.caught.concat(got.kept);
      paintTank(); paintPaper(); paintBowl();
      if (S.round.torn) done('torn');
      return got;
    },
    repaint: function () { paintTank(); paintPaper(); paintBowl(); },
  };
})();
